import {
    OnboardingStepFooter,
    type OnboardingTertiaryAction,
} from "@/features/onboarding/components/OnboardingStepFooter";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { ui } from "@patient-watch/ui";
import { authQueryOptions, getAuth } from "@/features/auth/api/useAuthQuery";
import { supabaseClient } from "@/lib/supabase";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { toast } from "@patient-watch/ui/sonner";
import * as z from "zod";
import { invalidateProfileQuery, patchProfileQueryData } from "@/features/profile/api/selectProfile";
import { clearOnboardingContactSkip } from "@/features/onboarding/utils/onboardingContactSkip";
import { useProfileCopy } from "@/features/i18n/profileCopy";
import { useToastCopy } from "@/features/i18n/toastCopy";

const formSchema = z.object({
    email: z.string().email("Please enter a valid email address"),
});

type FormSchemaType = z.infer<typeof formSchema>;

interface Props {
    userId: string;
    currentEmail: string | null;
    onSuccess?: () => void;
    onBack?: () => void;
    primaryLabel?: string;
    tertiary?: OnboardingTertiaryAction;
}

interface SetEmailResponse {
    success?: boolean;
    email?: string;
    error?: string;
}

interface UpdateEmailResponse {
    success?: boolean;
    message?: string;
    error?: string;
}

function messageFromFunctionsResult<T extends { error?: string }>(
    data: T | null,
    invokeError: Error | null
): string | null {
    if (data && typeof data.error === "string" && data.error.length > 0) {
        return data.error;
    }
    if (invokeError?.message) {
        return invokeError.message;
    }
    return null;
}

type SaveKind = "set" | "sync" | "update";

const OnboardingEmailForm = ({ userId, currentEmail, onSuccess, onBack, primaryLabel, tertiary }: Props) => {
    const copy = useProfileCopy();
    const toastCopy = useToastCopy();
    const queryClient = useQueryClient();
    const { data: authUser } = useQuery(authQueryOptions());

    const signInEmail = authUser?.email?.trim() ?? "";
    const profileEmail = currentEmail?.trim() ?? "";

    const form = useForm<FormSchemaType>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: profileEmail || signInEmail,
        },
    });

    useEffect(() => {
        const next = signInEmail || profileEmail;
        if (next) {
            form.setValue("email", next);
        }
    }, [signInEmail, profileEmail, form]);

    const saveEmailMutation = useMutation({
        mutationFn: async (submitted: string): Promise<SaveKind> => {
            const trimmed = submitted.trim();
            const sessionUser = await getAuth();
            const accountEmail = sessionUser?.email?.trim() ?? "";

            if (!accountEmail) {
                const { data, error } = await supabaseClient.functions.invoke<SetEmailResponse>(
                    "public-set-user-email",
                    {
                        body: { email: trimmed },
                    }
                );
                const fail = messageFromFunctionsResult(data, error);
                if (fail) {
                    throw new Error(fail);
                }
                if (!data?.success) {
                    throw new Error("Could not save email");
                }
                return "set";
            }

            if (trimmed.toLowerCase() === accountEmail.toLowerCase()) {
                await supabaseClient
                    .from("users")
                    .update({ email: trimmed })
                    .eq("user_id", userId)
                    .throwOnError()
                    .select()
                    .single();
                return "sync";
            }

            const { data, error } = await supabaseClient.functions.invoke<UpdateEmailResponse>(
                "public-update-user-email",
                {
                    body: { old_email: accountEmail, new_email: trimmed },
                }
            );
            const fail = messageFromFunctionsResult(data, error);
            if (fail) {
                throw new Error(fail);
            }
            if (!data?.success) {
                throw new Error("Could not update email");
            }
            return "update";
        },
        onSuccess: (kind, email) => {
            if (kind === "set") {
                toast.success(toastCopy.emailSetSuccess);
            } else if (kind === "sync") {
                toast.success(toastCopy.emailSavedToProfileToast);
            } else {
                toast.success(toastCopy.emailUpdatedSuccessToast);
            }
            patchProfileQueryData(queryClient, userId, { email });
            void clearOnboardingContactSkip(userId, "email")
                .then((userMetaData) => {
                    patchProfileQueryData(queryClient, userId, { user_meta_data: userMetaData });
                })
                .catch(() => undefined);
            void invalidateProfileQuery(queryClient, userId);
            void queryClient.invalidateQueries({ queryKey: ["user"] });
            onSuccess?.();
        },
        onError: (error: unknown) => {
            const message =
                error instanceof Error && error.message ? error.message : "Failed to save email. Please try again.";
            toast.error(message);
        },
    });

    const onSubmit: SubmitHandler<FormSchemaType> = (data) => {
        saveEmailMutation.mutate(data.email.trim());
    };

    return (
        <ui.Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="form">
                <fc.Email autoFocus label={copy.email} />
                <OnboardingStepFooter
                    onBack={onBack}
                    disabled={saveEmailMutation.isPending}
                    isLoading={saveEmailMutation.isPending}
                    primaryLabel={primaryLabel}
                    tertiary={tertiary}
                />
            </form>
        </ui.Form>
    );
};

export default OnboardingEmailForm;
